import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import timezones from 'timezones-list';

import useStates, { type UseStatesInterface } from '@/hooks/pure/useStates';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SCHEDULE_PERIOD } from '@/types/commonConst';
import {
  WORKFLOW_SUBSCRIPTION_BASIS,
  WORKFLOW_TYPE,
} from '@/types/workflowConst';

// 폼 입력 중 상태를 위한 draft 타입 (target은 undefined 허용)
export type Subscription2ContextState = {
  basis: number;
  target?: string;
  schedule: Schedule;
  timezone: string;
};

type Subscription2ContextProps = {
  triggerState: UseStatesInterface<Subscription2ContextState>;
  triggerValidated: ValidateInfoT;
  actionState: UseStatesInterface<SubscriptionCreateWorkflowBaseParam>;
  actionValidated: ValidateInfoT;
  mode: EDIT_MODE;
  initialState?: SubscriptionWorkflow;
  alias?: string;
  hasChange: boolean;
  setHasChange: (s: boolean) => void;
};

const getTimezoneList = () => {
  const timezoneInfos = [] as { name: string; tzCode: string }[];
  for (const timezone of timezones) {
    timezoneInfos.push({
      name: timezone.label,
      tzCode: timezone.tzCode,
    });
  }
  return timezoneInfos;
};

const defaultSubscriptionState: Subscription2ContextState = {
  target: undefined,
  basis: WORKFLOW_SUBSCRIPTION_BASIS.DASHBOARD2,
  schedule: {
    period: SCHEDULE_PERIOD.DAILY,
    hour: 0,
  },
  timezone: getTimezoneList()[0].tzCode,
};

const defaultState: SubscriptionCreateWorkflowBaseParam = {
  channel: undefined,
  title: 'Untitled',
  type: WORKFLOW_TYPE.SUBSCRIPTION,
  enable: true,
};

const Subscription2Context = createContext<
  Subscription2ContextProps | undefined
>(undefined);

type Props = {
  initialState?: SubscriptionWorkflow;
  mode: EDIT_MODE;
  children: ReactNode;
};

const Subscription2ContextProvider = (props: Props) => {
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const subscriptionStates = useStates<Subscription2ContextState>({
    ...defaultSubscriptionState,
    timezone: currWorkspace?.timezone || defaultSubscriptionState.timezone,
  });
  const actionStates =
    useStates<SubscriptionCreateWorkflowBaseParam>(defaultState);
  const state = subscriptionStates.state;

  const [hasChange, setHasChange] = useState(false);

  useEffect(() => {
    if (props.initialState) {
      const { subscription, ...rest } = props.initialState;
      subscriptionStates.setState({
        ...subscription,
      });
      actionStates.setState(rest);
    }
    setHasChange(false);
  }, [props.initialState]);

  const triggerValidated = useMemo(() => {
    if (!state.basis)
      return {
        validated: false,
        message: '',
      };
    if (typeof state.target === 'undefined')
      return {
        validated: false,
        message: 'Dashboard must be selected',
      };
    return {
      validated: true,
    };
  }, [state.basis, state.target]);

  const actionValidated = useMemo(() => {
    if (!actionStates.state.channel)
      return {
        validated: false,
        message: 'Notification channel must be configured.',
      };

    return {
      validated: true,
    };
  }, [actionStates.state.channel]);

  return (
    <Subscription2Context.Provider
      value={{
        triggerState: {
          ...subscriptionStates,
          setValue(key, value) {
            setHasChange(true);
            subscriptionStates.setValue(key, value);
          },
          setState: (s) => {
            setHasChange(true);
            subscriptionStates.setState(s);
          },
        },
        triggerValidated,
        actionState: {
          ...actionStates,
          setValue(key, value) {
            setHasChange(true);
            actionStates.setValue(key, value);
          },
          setState: (s) => {
            setHasChange(true);
            actionStates.setState(s);
          },
        },
        actionValidated,
        mode: props.mode,
        initialState: props.initialState,
        alias: props.initialState?.alias,
        hasChange,
        setHasChange,
      }}
    >
      {props.children}
    </Subscription2Context.Provider>
  );
};

export const useSubscription2Context = (): Subscription2ContextProps =>
  useContext(Subscription2Context)!;

export default Subscription2ContextProvider;
